import { ReactNode, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';

import { Button, FormInput, Text } from '@calm-web/design-system';

import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';
import { useAuthFormInputs } from '@/hooks/forms/useAuthFormInputs';
import { useDeviceState, useUserState } from '@/hooks/store';
import genericMessages from '@/messages/messages';
import { calmLogger } from '@/utils/calmLogger';
import { postMe } from '@/utils/endpoints';
import { determineComplianceLevel } from '@/utils/getInitialState/initComplianceLevel';
import { declineGdprCookies, declineOptInCookies, removeAllCookies } from '@/utils/privacyCookies';

import messages from './messages';
import {
	Container,
	FormWrapper,
	HelperLink,
	MainGrid,
	OptoutButtonWrapper,
	SuccessTextWrapper,
} from './styles';

const OptOut = (): JSX.Element => {
	const apiRequest = useApi();
	const user = useUserState();
	const device = useDeviceState();
	const { formatMessage } = useIntl();
	const { logEvent, logEventAsync } = useAnalytics();
	const [{ email }, inputActions] = useAuthFormInputs(
		['email'] as const,
		{
			email: '',
		},
		false,
		{
			mode: 'password-reset',
		},
	);
	const [isSubmittingOptout, setIsSubmittingOptout] = useState(false);
	const [isSubmitOptoutFailed, setIsSubmitOptoutFailed] = useState(false);
	const [isSubmitOptoutSucceeded, setIsSubmitOptoutSucceeded] = useState(false);
	const [isCookieOptoutSucceeded, setIsCookieOptoutSucceeded] = useState(false);
	const requireGdprChoice = device.requires_gdpr_choice;

	const getOptOutButtonText = (): string => {
		if (isCookieOptoutSucceeded) {
			return formatMessage(messages.optOutOfTargetingCookiesSuccess);
		}
		return formatMessage(messages.optOutOfTargetingCookiesCta);
	};

	const getSubmitButtonText = (): string => {
		if (isSubmitOptoutSucceeded) {
			return formatMessage(messages.submitSuccess);
		}
		if (isSubmitOptoutFailed) {
			return formatMessage(messages.submitError);
		}
		return formatMessage(messages.submitCta);
	};

	const getSubmitButtonDisabled = (): boolean => {
		if (isSubmittingOptout) {
			return true;
		}
		if (isSubmitOptoutSucceeded) {
			return true;
		}
		if (isSubmitOptoutFailed) {
			return true;
		}
		return email.value.length === 0 || !email.isValid;
	};

	const submitOptoutRequest = async () => {
		logEvent({ eventName: 'Ads Optout : Clicked' });
		setIsSubmittingOptout(true);
		try {
			await apiRequest({
				method: 'POST',
				endpoint: 'ads-optout',
				body: {
					email: email.value,
				},
			});
			setIsSubmitOptoutSucceeded(true);
			logEvent({ eventName: 'Ads Optout : Success' });
		} catch (err) {
			setIsSubmitOptoutFailed(true);
			logEvent({ eventName: 'Ads Optout : Error' });
		}
		setIsSubmittingOptout(false);
	};

	const optOutOfTargetingCookies = async (): Promise<void> => {
		removeAllCookies();
		if (requireGdprChoice) {
			declineGdprCookies();
		} else {
			declineOptInCookies();
		}
		setIsCookieOptoutSucceeded(true);

		if (user) {
			const newComplianceLevel = determineComplianceLevel({
				requiresGdpr: !!requireGdprChoice,
				isLimitedData: !requireGdprChoice,
				hasDeclinedGdprCookies: !!requireGdprChoice,
				isHipaaUser: user?.is_hipaa_compliant,
			});

			await postMe({
				has_opted_in_limited_data_use: true,
				compliance_level: newComplianceLevel,
			}).catch(error => calmLogger.error('Error in OptOut postMe', {}, error));
		}

		try {
			await logEventAsync({
				eventName: 'Cookie Preferences : Essential Only',
			});
		} catch (error) {
			calmLogger.error('Error in OptOut logEventAsync', {}, error);
		}
		window.location.reload();
	};

	return (
		<MainGrid>
			<Container>
				<p>{formatMessage(messages.intro)}</p>
				<i>{formatMessage(messages.cookiesTitle)}</i>
				<p>{formatMessage(messages.cookies)}</p>
				<OptoutButtonWrapper>
					<Button
						backgroundColor="blue2"
						onPress={optOutOfTargetingCookies}
						isDisabled={isCookieOptoutSucceeded}
						fullWidth
					>
						{getOptOutButtonText()}
					</Button>
				</OptoutButtonWrapper>
				<br></br>
				<i>{formatMessage(messages.mobileAppTitle)}</i>
				<p>{formatMessage(messages.mobileApp)}</p>
				<i>{formatMessage(messages.accountInformationTitle)}</i>
				<p>{formatMessage(messages.accountInformation)}</p>
				<FormWrapper>
					<FormInput
						noMargin
						name="email"
						onBlur={inputActions.onBlur}
						onChange={inputActions.onChange}
						onFocus={inputActions.onFocus}
						value={email.value}
						isValid={email.isValid}
						label={formatMessage(genericMessages.emailPlaceholder)}
						error={email.errorMessage}
					/>
					<br />
					<Button
						fullWidth
						backgroundColor="blue2"
						onPress={submitOptoutRequest}
						isDisabled={getSubmitButtonDisabled()}
					>
						{getSubmitButtonText()}
					</Button>
				</FormWrapper>
				<SuccessTextWrapper isVisible={isSubmitOptoutSucceeded}>
					<Text>
						{formatMessage(messages.submitSuccessInformation, {
							externallink: (...chunks: ReactNode[]) => (
								<HelperLink href={'https://calm.com/privacy'} target="_blank" rel="noopener noreferrer">
									{formatMessage(messages.privacyLinkText)}
								</HelperLink>
							),
						})}
					</Text>
				</SuccessTextWrapper>
			</Container>
		</MainGrid>
	);
};

export default OptOut;
